import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createPractice, getPractice, listOrganizations, updatePractice } from '../../../api/admin'
import { Button } from '../../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../../components/ui/Card'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import { useToast } from '../../../components/ui/Toast'
import type { Organization, PracticeCreate } from '../../../types/admin'

const TIMEZONES = [
  'UTC',
  'Africa/Johannesburg',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Asia/Singapore',
  'Asia/Tokyo',
]

export default function PracticeForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const toast = useToast()

  const [name, setName] = useState('')
  const [orgId, setOrgId] = useState<string>('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [address1, setAddress1] = useState('')
  const [address2, setAddress2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [country, setCountry] = useState('')
  const [timezone, setTimezone] = useState('UTC')
  const [isActive, setIsActive] = useState(true)
  const [vatRate, setVatRate] = useState('15')

  const [orgs, setOrgs] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'name' | 'email', string>>>({})

  useEffect(() => {
    const loadData = async () => {
      try {
        const [orgData, practiceData] = await Promise.all([
          listOrganizations({ limit: 200 }),
          isEdit ? getPractice(Number(id)) : Promise.resolve(null),
        ])
        setOrgs(orgData.items)
        if (practiceData) {
          setName(practiceData.name)
          setOrgId(practiceData.organization_id != null ? String(practiceData.organization_id) : '')
          setPhone(practiceData.phone ?? '')
          setEmail(practiceData.email ?? '')
          setAddress1(practiceData.address_line1 ?? '')
          setAddress2(practiceData.address_line2 ?? '')
          setCity(practiceData.city ?? '')
          setState(practiceData.state ?? '')
          setPostalCode(practiceData.postal_code ?? '')
          setCountry(practiceData.country ?? '')
          setTimezone(practiceData.timezone ?? 'UTC')
          setIsActive(practiceData.is_active)
          setVatRate((parseFloat(practiceData.default_vat_rate) * 100).toFixed(2))
        }
      } catch {
        setError('Failed to load form data.')
      } finally {
        setLoading(false)
      }
    }
    void loadData()
  }, [id, isEdit])

  const validate = (): boolean => {
    const errs: typeof fieldErrors = {}
    if (!name.trim()) errs.name = 'Name is required.'
    if (email && !/^[^@]+@[^@]+\.[^@]+$/.test(email.trim())) errs.email = 'Enter a valid email address.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!validate()) return
    setSubmitting(true)
    setError(null)
    const payload: PracticeCreate = {
      name: name.trim(),
      organization_id: orgId ? Number(orgId) : null,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      address_line1: address1.trim() || undefined,
      address_line2: address2.trim() || undefined,
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      postal_code: postalCode.trim() || undefined,
      country: country.trim() || undefined,
      timezone,
      is_active: isActive,
      default_vat_rate: (parseFloat(vatRate) || 0) / 100,
    }
    try {
      if (isEdit) {
        await updatePractice(Number(id), payload)
        toast.success('Practice updated.')
      } else {
        await createPractice(payload)
        toast.success('Practice created.')
      }
      void navigate('/admin/practices')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 p-8">Loading…</div>

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate('/admin/practices')}
          className="bg-transparent border-0 cursor-pointer text-accent text-sm font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="text-[1.4rem] font-bold text-text-heading m-0">
          {isEdit ? 'Edit Practice' : 'New Practice'}
        </h1>
      </div>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <Card className="mb-5">
          <SectionTitle>Basic Info</SectionTitle>

          <FormField label="Practice name" required error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: undefined })) }}
              className={inputClass(Boolean(fieldErrors.name))}
            />
          </FormField>

          <FormField label="Organisation" hint="Optional — leave blank for standalone practice">
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className={selectClass()}
            >
              <option value="">— None —</option>
              {orgs.map((org) => (
                <option key={org.id} value={String(org.id)}>{org.name}</option>
              ))}
            </select>
          </FormField>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Phone">
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass()} />
            </FormField>
            <FormField label="Email" error={fieldErrors.email}>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: undefined })) }}
                className={inputClass(Boolean(fieldErrors.email))}
              />
            </FormField>
          </div>

          <FormField label="Timezone">
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={selectClass()}>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </FormField>

          <FormField label="VAT rate (%)" hint="Pre-fills the tax rate on new invoices for this practice">
            <input
              type="number"
              value={vatRate}
              onChange={(e) => setVatRate(e.target.value)}
              className={inputClass()}
              min={0} max={100} step={0.01}
            />
          </FormField>

          <FormField label="Status">
            <label className="flex items-center gap-2 cursor-pointer text-[0.9rem]">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
          </FormField>
        </Card>

        <Card className="mb-5">
          <SectionTitle>Address</SectionTitle>
          <FormField label="Address line 1">
            <input type="text" value={address1} onChange={(e) => setAddress1(e.target.value)} className={inputClass()} />
          </FormField>
          <FormField label="Address line 2">
            <input type="text" value={address2} onChange={(e) => setAddress2(e.target.value)} className={inputClass()} />
          </FormField>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="City">
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputClass()} />
            </FormField>
            <FormField label="State / Province">
              <input type="text" value={state} onChange={(e) => setState(e.target.value)} className={inputClass()} />
            </FormField>
            <FormField label="Postal code">
              <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className={inputClass()} />
            </FormField>
            <FormField label="Country">
              <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} className={inputClass()} />
            </FormField>
          </div>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Practice'}
          </Button>
          <Button type="button" variant="outline" onClick={() => void navigate('/admin/practices')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
